/**
 * Arlo & Co — Font Selector
 * Renders a clickable font-choice UI on product pages that have multiple
 * fonts configured. Integrates with arlo-input-autofit.js and
 * arlo-canvas-capture.js.
 *
 * Requires: arlo-product-config.js loaded first.
 *
 * UX flow:
 *   1. Customer types their name/text → sees it in the default font
 *   2. Customer clicks a font swatch → live preview updates instantly
 *   3. Selected font is recorded in a hidden input (picked up by canvas capture)
 */

(function () {
  'use strict';

  // ── Styles ──────────────────────────────────────────────────────────────────
  const CSS = `
    .arlo-font-selector {
      margin-top: 20px;
      margin-bottom: 4px;
    }
    .arlo-font-selector__label {
      font-size: 13px;
      font-weight: 600;
      color: #555;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 10px;
    }
    .arlo-font-selector__grid {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    .arlo-font-pill {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 10px 16px;
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      cursor: pointer;
      background: #fff;
      transition: border-color 0.15s, background 0.15s;
      min-width: 100px;
      user-select: none;
      -webkit-user-select: none;
    }
    .arlo-font-pill:hover {
      border-color: #aaa;
    }
    .arlo-font-pill.active {
      border-color: #222;
      background: #f5f5f5;
    }
    .arlo-font-pill__sample {
      font-size: 26px;
      line-height: 1.2;
      pointer-events: none;
    }
    .arlo-font-pill__name {
      font-size: 10px;
      font-family: -apple-system, sans-serif;
      color: #888;
      margin-top: 4px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      pointer-events: none;
    }
  `;

  function injectStyles() {
    if (document.getElementById('arlo-font-selector-styles')) return;
    const style = document.createElement('style');
    style.id = 'arlo-font-selector-styles';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // ── Font loading ─────────────────────────────────────────────────────────────
  async function loadFont(fontId, files) {
    if (document.fonts.check(`12px '${fontId}'`)) return;
    for (const file of files) {
      try {
        const face = new FontFace(fontId, `url('${file.url}')`);
        const loaded = await face.load();
        document.fonts.add(loaded);
        await document.fonts.ready;
        return;
      } catch (e) { /* try next format */ }
    }
  }

  // ── Apply selected font to all visible text inputs ──────────────────────────
  // NOTE: visible inputs use [data-custom-option-input] and live OUTSIDE the form.
  // The form contains hidden mirror inputs with [data-custom-option-target].
  function applyFontToInputs(fontId) {
    document.querySelectorAll('[data-custom-option-input]').forEach(function (input) {
      if (input.tagName === 'TEXTAREA') return;
      input.style.fontFamily = `'${fontId}', sans-serif`;
      if (window._arloFitInput) {
        window._arloFitInput(input);
      } else {
        input.dispatchEvent(new Event('input'));
      }
    });
  }

  // ── Set/update the hidden font property inside the cart form ─────────────────
  function setFontProperty(form, fontId) {
    let hidden = form.querySelector('[data-arlo-font]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[Font]';
      hidden.setAttribute('data-arlo-font', '1');
      form.appendChild(hidden);
    }
    hidden.value = fontId;
  }

  // ── Trigger canvas re-render if canvas capture is present ───────────────────
  function triggerCanvasUpdate() {
    if (window._arloTriggerCanvasUpdate) {
      window._arloTriggerCanvasUpdate();
    }
  }

  // ── Build the selector UI ────────────────────────────────────────────────────
  function buildSelector(fonts, form, productConfig) {
    // Load all fonts in parallel so swatches render correctly
    fonts.forEach(function (f) { loadFont(f.id, f.files); });

    const wrapper = document.createElement('div');
    wrapper.className = 'arlo-font-selector';

    const label = document.createElement('div');
    label.className = 'arlo-font-selector__label';
    label.textContent = 'Choose your font style';
    wrapper.appendChild(label);

    const grid = document.createElement('div');
    grid.className = 'arlo-font-selector__grid';
    wrapper.appendChild(grid);

    let activeFontId = fonts[0].id;

    function selectFont(fontId, pill) {
      activeFontId = fontId;

      // Update active pill
      grid.querySelectorAll('.arlo-font-pill').forEach(function (p) {
        p.classList.toggle('active', p.dataset.fontId === fontId);
      });

      // Apply font to inputs
      applyFontToInputs(fontId);

      // Record the selection
      setFontProperty(form, fontId);

      // Update canvas if applicable
      triggerCanvasUpdate();
    }

    fonts.forEach(function (f) {
      const pill = document.createElement('div');
      pill.className = 'arlo-font-pill' + (f.id === activeFontId ? ' active' : '');
      pill.dataset.fontId = f.id;

      const sample = document.createElement('div');
      sample.className = 'arlo-font-pill__sample';
      sample.style.fontFamily = `'${f.id}', sans-serif`;
      // Use the font's display name as sample text, or generic "Arlo"
      sample.textContent = f.sampleText || 'Arlo';

      const name = document.createElement('div');
      name.className = 'arlo-font-pill__name';
      name.textContent = f.displayName || f.name || f.id;

      pill.appendChild(sample);
      pill.appendChild(name);

      pill.addEventListener('click', function () {
        selectFont(f.id, pill);
      });

      grid.appendChild(pill);
    });

    // Set the default font property immediately
    setFontProperty(form, activeFontId);

    return wrapper;
  }

  // ── Find insertion point — right after the last visible input block ──────────
  // IMPORTANT: In the Artisan theme, visible inputs (data-custom-option-input)
  // are rendered OUTSIDE the <form> in block-custom-id-* divs. We search the
  // whole document for these, not just inside the form.
  function findInsertionPoint() {
    const inputs = document.querySelectorAll('[data-custom-option-input]');
    if (!inputs.length) return null;
    const lastInput = inputs[inputs.length - 1];

    // Walk up to find the block-custom-id-* wrapper div
    let el = lastInput;
    while (el && el.parentElement) {
      if (el.className && typeof el.className === 'string' && el.className.includes('block-custom-id-')) {
        return el;
      }
      el = el.parentElement;
    }

    // Fallback: direct parent of the input
    return lastInput.parentElement || lastInput;
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  function init(config) {
    const handle = getProductHandle();
    if (!handle) return;

    const productConfig = config.products[handle];
    if (!productConfig) return;

    const fonts = productConfig.fonts;
    // Only show the selector if there are 2 or more fonts to choose from
    if (!fonts || fonts.length < 2) {
      // Still set the default font property so canvas capture has it
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form && fonts && fonts[0]) setFontProperty(form, fonts[0].id);
      return;
    }

    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    injectStyles();

    const selector = buildSelector(fonts, form, productConfig);

    // Insert after the last visible input block (which lives outside the form)
    const anchor = findInsertionPoint();
    if (anchor && anchor.parentElement) {
      anchor.parentElement.insertBefore(selector, anchor.nextSibling);
    } else {
      // Fallback: insert before the add-to-cart button inside the form
      const btn = form.querySelector('[type="submit"]');
      if (btn) btn.parentElement.insertBefore(selector, btn);
      else form.appendChild(selector);
    }

    // Apply the default font to inputs immediately (after fonts load)
    document.fonts.ready.then(function () {
      applyFontToInputs(fonts[0].id);
    });
  }

  function getProductHandle() {
    if (window.ShopifyAnalytics?.meta?.product?.handle) {
      return window.ShopifyAnalytics.meta.product.handle;
    }
    const metaHandle = document.querySelector('meta[name="product-handle"]');
    if (metaHandle) return metaHandle.getAttribute('content');
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function waitForConfig(cb, attempts) {
    attempts = attempts || 0;
    if (window.ArloProductConfig) return cb(window.ArloProductConfig);
    if (attempts > 20) return;
    setTimeout(function () { waitForConfig(cb, attempts + 1); }, 100);
  }

  document.addEventListener('DOMContentLoaded', function () {
    waitForConfig(init);
  });

})();
